import { useMemo, useState, useEffect } from 'react';
import { FileText, MagnifyingGlass, X } from '@phosphor-icons/react';
import { useTranslation } from 'react-i18next';
import { Stack, Typography } from '@mui/material';

import { i18n } from 'i18n';
import {
  InternalPage,
  Loader,
  NoResultsMessage,
  Spacer,
  Input,
  IconButton,
} from 'theSource/components';
import { useApiQuery } from 'theSource/utils';
import { GET_BENEFIT_DOCUMENT_DATA_QUERY_KEY } from './utils';
import { BenefitDocumentsResponseDto } from './types';
import { mapBenefitDocuments, filterDocuments } from './utils/mappingUtils';
import { BenefitDocumentsList } from './components';
import { BackLink } from 'enrollment/components';

export const BenefitDocuments = () => {
  const { t } = useTranslation('pages.benefitDocuments');
  const { data, isLoading } = useApiQuery<BenefitDocumentsResponseDto>({
    queryKey: GET_BENEFIT_DOCUMENT_DATA_QUERY_KEY,
    endpoint: '/document_library/benefit_documents',
  });
  const [filter, setFilter] = useState<string>('');
  const [length, setLength] = useState<number>(1);
  const benefitDocuments = useMemo(
    () => data && mapBenefitDocuments(data.data),
    [data]
  );

  useEffect(() => {
    if (benefitDocuments) {
      setLength(
        benefitDocuments.trees
          .map((document) => filterDocuments(document.children, filter).length)
          .reduce((partialSum: number, a: number) => partialSum + a, 0)
      );
    }
  }, [benefitDocuments, filter]);

  if (!benefitDocuments || isLoading) {
    return <Loader />;
  }
  return (
    <InternalPage
      testId="benefit-documents"
      lookupCode="sub/enroll_documents"
      CustomActionComponent={<BackLink />}
      customActionAlignment="left"
    >
      <Stack spacing={2}>
        <Input
          id="benefit-documents-search"
          placeholder={i18n.t('common:search')}
          onChange={(event) => {
            setFilter(event.target.value);
          }}
          value={filter}
          endAdornment={
            <IconButton
              Icon={filter ? X : MagnifyingGlass}
              onClick={() => {
                setFilter('');
              }}
              variant="tertiary"
              aria-label={
                filter ? i18n.t('common:clearFilter') : i18n.t('common:search')
              }
            />
          }
        />
        {filter ? (
          <>
            <Typography variant="body1">
              Showing {length} results for "{filter}"
            </Typography>
            <Spacer height={1} />
          </>
        ) : (
          <Spacer height={9} />
        )}
      </Stack>
      {benefitDocuments.trees.length > 0 && length > 0 ? (
        <Stack spacing={2}>
          {benefitDocuments.trees.map((document, index) => {
            return (
              <BenefitDocumentsList
                key={index}
                data={filterDocuments(document.children, filter)}
                title={document.title}
                hideTitle={false}
              />
            );
          })}
        </Stack>
      ) : (
        <>
          <Spacer height={7} />
          <NoResultsMessage
            IconComponent={FileText}
            message={t('noDocumentsMessage')}
            testId="no-documents-message"
          />
        </>
      )}
    </InternalPage>
  );
};
